/**
 * IncompleteDependenciesWarningModal component.
 * 
 * Displays when a user tries to mark a task as complete
 * while it still has incomplete predecessor dependencies.
 * Provides option to override and complete anyway.
 */

import React from 'react';
import Button from './Button';
import Card from './Card';

interface IncompleteDependenciesWarningModalProps {
  taskTitle: string;
  incompleteDependencies: Array<{
    task_id: string;
    task_title: string;
    task_status: string;
  }>;
  onConfirmOverride: () => void;
  onCancel: () => void;
}

const IncompleteDependenciesWarningModal: React.FC<IncompleteDependenciesWarningModalProps> = ({
  taskTitle,
  incompleteDependencies,
  onConfirmOverride,
  onCancel,
}) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
        <Card>
          <Card.Header
            title="⚠️ Incomplete Dependencies"
            subtitle="This task has incomplete predecessor tasks"
          />
          
          <Card.Body>
            <div className="mb-4">
              <p className="text-gray-800 mb-4">
                You are trying to mark <strong>"{taskTitle}"</strong> as complete, but it has{' '}
                <strong>{incompleteDependencies.length}</strong> incomplete predecessor{' '}
                {incompleteDependencies.length === 1 ? 'task' : 'tasks'} that should be completed first:
              </p>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <ul className="space-y-2">
                  {incompleteDependencies.map(dep => (
                    <li key={dep.task_id} className="flex items-center gap-3">
                      <span className={`badge ${
                        dep.task_status === 'in_progress' 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {dep.task_status.replace('_', ' ')}
                      </span>
                      <span className="text-gray-900">{dep.task_title}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
              <p className="text-sm text-blue-900">
                <strong>Best Practice:</strong> Complete all predecessor tasks before marking this task as complete.
                This ensures proper task flow and accurate project progress tracking.
              </p>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-4">
              <p className="text-sm text-gray-700">
                <strong>Override Option:</strong> You can choose to override this warning and mark the task complete anyway.
                Use this only if you have a valid reason to complete tasks out of dependency order.
              </p>
            </div>
          </Card.Body>

          <Card.Footer>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onCancel}>
                Cancel - Keep Status
              </Button>
              <Button variant="danger" onClick={onConfirmOverride}>
                Override - Complete Anyway
              </Button>
            </div>
          </Card.Footer>
        </Card>
      </div>
    </div>
  );
};

export default IncompleteDependenciesWarningModal;
